import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput, validatePhone } from "@/components/ui/phone-input";
import AvatarPicker from "@/components/AvatarPicker";
import { type GiftPreferences } from "@/components/card/GiftGuidanceForm";
import GamifiedPreferencesEditor from "@/components/profile/GamifiedPreferencesEditor";
import MyDatesTab from "@/components/profile/MyDatesTab";
import { Loader2, User, Gift, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === "gift-preferences"
      ? "gift-preferences"
      : tabParam === "my-dates"
        ? "my-dates"
        : "profile";

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [giftPrefs, setGiftPrefs] = useState<GiftPreferences>({
    wants: "",
    has: "",
    style: "",
    registries: [],
    priceGuidance: "",
    showInInvite: true,
  });
  const [dislikes, setDislikes] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        setDisplayName(data.display_name || "");
        setAvatarUrl(
          data.avatar_url ||
            `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(user.email || user.id)}`
        );
        setPhone(data.phone || "");
        setAge(data.age != null ? String(data.age) : "");
        setGender(data.gender || "");
        setGiftPrefs({
          wants: (data as any).preferences_wants || "",
          has: (data as any).preferences_has || "",
          style: (data as any).preferences_style || "",
          registries: ((data as any).preferences_registries as string[]) || [],
          priceGuidance: (data as any).preferences_price_guidance || "",
          showInInvite: true,
        });
        setDislikes((data as any).preferences_dislikes || "");
      }
      setLoaded(true);
    };
    load();
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast({ variant: "destructive", title: "Invalid file type", description: "Please use JPG, PNG, or WebP." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ variant: "destructive", title: "File too large", description: "Maximum size is 5 MB." });
      return;
    }
    setUploading(true);
    const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file);
    if (error) {
      toast({ variant: "destructive", title: "Upload failed", description: error.message });
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    setAvatarUrl(urlData.publicUrl);
    setUploading(false);
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    const phoneErr = validatePhone(phone);
    if (phoneErr) { setPhoneError(phoneErr); return; }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: displayName.trim() || null,
        avatar_url: avatarUrl || null,
        phone: phone.trim() || null,
        age: age ? parseInt(age, 10) : null,
        gender: gender || null,
      } as any)
      .eq("user_id", user.id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not save profile." });
    } else {
      toast({ title: "Saved!", description: "Your profile has been updated." });
    }
    setSaving(false);
  };

  const persistGiftPrefs = async (
    prefs: GiftPreferences,
    dislikesValue: string,
  ) => {
    if (!user) return { error: null as null };
    return supabase
      .from("profiles")
      .update({
        preferences_wants: prefs.wants || null,
        preferences_has: prefs.has || null,
        preferences_style: prefs.style || null,
        preferences_registries: prefs.registries.filter(Boolean) as any,
        preferences_price_guidance: prefs.priceGuidance || null,
        preferences_dislikes: dislikesValue || null,
      } as any)
      .eq("user_id", user.id);
  };

  const handleSaveGiftPrefs = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await persistGiftPrefs(giftPrefs, dislikes);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not save preferences." });
    } else {
      toast({ title: "Saved!", description: "Your gift preferences have been updated." });
    }
    setSaving(false);
  };

  const handleAutoSavePrefs = async (prefs: GiftPreferences) => {
    if (!user) return;
    await persistGiftPrefs(prefs, dislikes);
  };

  if (authLoading || !loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  const currentAvatar =
    avatarUrl ||
    `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(user?.email || user?.id || "")}`;

  const tabs: { key: "profile" | "gift-preferences" | "my-dates"; label: string; icon: typeof User }[] = [
    {
      key: "profile",
      label: t("profile.tabProfile", { defaultValue: "Profile" }),
      icon: User,
    },
    {
      key: "gift-preferences",
      label: t("profile.tabGiftPrefs", { defaultValue: "Gift Preferences" }),
      icon: Gift,
    },
    {
      key: "my-dates",
      label: t("profile.tabMyDates", { defaultValue: "My Dates" }),
      icon: Calendar,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader onBack="/dashboard" userMenuVariant="dashboard" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground font-serif mb-6">
          {t("profile.title", { defaultValue: "My Profile" })}
        </h1>

        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
          <aside className="sm:w-56 sm:shrink-0">
            <nav
              className="flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-visible -mx-1 px-1 sm:m-0 sm:p-0"
              aria-label={t("profile.title", { defaultValue: "My Profile" })}
            >
              {tabs.map(({ key, label, icon: Icon }) => {
                const active = activeTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => setSearchParams({ tab: key }, { replace: true })}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-left whitespace-nowrap sm:w-full",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                    )}
                    aria-current={active ? "page" : undefined}
                  >
                    <Icon size={16} className="shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="flex-1 min-w-0">
            {activeTab === "profile" && (
              <div className="space-y-6">
                <AvatarPicker
                  currentUrl={currentAvatar}
                  uploading={uploading}
                  onSelectPreset={(url) => setAvatarUrl(url)}
                  onUploadFile={(file) => {
                    handleAvatarUpload({ target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>);
                  }}
                />

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    {t("profile.displayName", { defaultValue: "Display name" })}
                  </Label>
                  <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" className="border-border" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    {t("profile.email", { defaultValue: "Email" })}
                  </Label>
                  <Input value={user?.email || ""} disabled className="border-border bg-muted/50 text-muted-foreground" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    {t("profile.phone", { defaultValue: "Mobile number" })}
                  </Label>
                  <PhoneInput
                    value={phone}
                    onChange={(val) => { setPhone(val); setPhoneError(validatePhone(val)); }}
                    error={phoneError || undefined}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    {t("profile.age", { defaultValue: "Age" })}
                  </Label>
                  <Input value={age} onChange={(e) => setAge(e.target.value.replace(/\D/g, ""))} placeholder="25" type="number" min={1} max={150} className="border-border" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    {t("profile.gender", { defaultValue: "Gender" })}
                  </Label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
                  >
                    <option value="">{t("profile.genderPlaceholder", { defaultValue: "Select gender" })}</option>
                    <option value="male">{t("profile.male", { defaultValue: "Male" })}</option>
                    <option value="female">{t("profile.female", { defaultValue: "Female" })}</option>
                    <option value="other">{t("profile.other", { defaultValue: "Other" })}</option>
                    <option value="prefer_not_to_say">{t("profile.preferNotToSay", { defaultValue: "Prefer not to say" })}</option>
                  </select>
                </div>

                <Button onClick={handleSaveProfile} disabled={saving} size="lg" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {t("profile.save", { defaultValue: "Save Profile" })}
                </Button>
              </div>
            )}

            {activeTab === "gift-preferences" && (
              <div>
                <p className="text-sm text-muted-foreground mb-6">
                  {t("profile.giftPrefsDesc", {
                    defaultValue:
                      "Tap chips, type your own — your friends will use this to pick gifts you'll actually love. Edit it any time.",
                  })}
                </p>
                <GamifiedPreferencesEditor
                  preferences={giftPrefs}
                  onChange={setGiftPrefs}
                  onAutoSave={handleAutoSavePrefs}
                  dislikes={dislikes}
                  onDislikesChange={setDislikes}
                />
                <Button
                  onClick={handleSaveGiftPrefs}
                  disabled={saving}
                  size="lg"
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 mt-6"
                >
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {t("profile.savePrefs", { defaultValue: "Save Preferences" })}
                </Button>
              </div>
            )}

            {activeTab === "my-dates" && user && (
              <MyDatesTab userId={user.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
